import {
    type ActivityItem,
    AppShell,
    bindShortcut,
    ExtensionHostBootstrap,
    getCommonCommands,
    getThemeAccentStyle,
    PushButton,
    registerCommand,
    useT,
    useEditorStore,
    useKeyboardShortcuts,
    WorkbenchI18nProvider,
} from "@magic-md/editor-core";
import {
    Cpu,
    FolderOpen,
    FolderTree,
    Globe,
    HardDrive,
    List,
    Search,
    Settings2,
    Upload,
    WifiOff,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { EngineConnectorPanel } from "./components/EngineConnectorPanel";
import { useEngineConnectorStore } from "./platform/engine-connector";

const sidebarActivityItems: ActivityItem[] = [
    {
        id: "files",
        icon: <FolderOpen size={18} />,
        label: "Open Files",
        labelKey: "sidebar.openFiles",
    },
    {
        id: "explorer",
        icon: <FolderTree size={18} />,
        label: "Explorer",
        labelKey: "sidebar.explorer",
    },
    {
        id: "toc",
        icon: <List size={18} />,
        label: "Table of Contents",
        labelKey: "sidebar.toc",
    },
    {
        id: "search",
        icon: <Search size={18} />,
        label: "Search",
        labelKey: "sidebar.search",
    },
    {
        id: "publish",
        icon: <Upload size={18} />,
        label: "Publish",
        labelKey: "sidebar.publish",
    },
];

/* ── App Shell ── */

const WebSidebarBottomSlot = memo(function WebSidebarBottomSlot({
    connectorOpen,
    engineConnected,
    engineMode,
    enginePanelActive,
    onToggleConnector,
    settingsActive,
}: {
    connectorOpen: boolean;
    engineConnected: boolean;
    engineMode: string;
    enginePanelActive: boolean;
    onToggleConnector: () => void;
    settingsActive: boolean;
}) {
    const t = useT("editor");

    return (
        <div className="flex flex-col gap-1">
            <button
                type="button"
                title={t("sidebar.engine")}
                data-testid="engine-connector-button"
                data-engine-connected={engineConnected ? "true" : "false"}
                data-engine-mode={engineMode}
                onClick={() => useEditorStore.getState().setSidebarView("engine")}
                className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors ${enginePanelActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    }`}
            >
                <Cpu size={18} />
            </button>
            <button
                type="button"
                title={t("sidebar.settings")}
                onClick={() => useEditorStore.getState().setSidebarView("settings")}
                className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors ${settingsActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    }`}
            >
                <Settings2 size={18} />
            </button>
            <button
                type="button"
                title={t("sidebar.engineConnector")}
                data-engine-connector-trigger="true"
                onClick={onToggleConnector}
                className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors ${connectorOpen
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    }`}
            >
                {engineConnected ? (
                    engineMode === "local" ? (
                        <HardDrive
                            size={18}
                            className="theme-accent-text"
                            style={getThemeAccentStyle("success")}
                        />
                    ) : (
                        <Globe
                            size={18}
                            className="theme-accent-text"
                            style={getThemeAccentStyle("info")}
                        />
                    )
                ) : (
                    <WifiOff size={18} />
                )}
            </button>
        </div>
    );
});

export function App() {
    return (
        <WorkbenchI18nProvider>
            <WebAppInner />
        </WorkbenchI18nProvider>
    );
}

function WebAppInner() {
    const t = useT("editor");
    const [connectorOpen, setConnectorOpen] = useState(false);
    const engineMode = useEngineConnectorStore((s) => s.mode);
    const engineConnected = useEngineConnectorStore((s) => s.connected);
    const sidebarOpen = useEditorStore((s) => s.sidebarOpen);
    const sidebarView = useEditorStore((s) => s.sidebarView);
    const enginePanelActive = sidebarOpen && sidebarView === "engine";
    const settingsActive = sidebarOpen && sidebarView === "settings";
    const toggleConnector = useCallback(() => {
        setConnectorOpen((open) => !open);
    }, []);
    const closeConnector = useCallback(() => {
        setConnectorOpen(false);
    }, []);
    const toolbarSlot = useMemo(
        () => (engineMode === "local" ? <PushButton /> : undefined),
        [engineMode],
    );
    const sidebarBottomSlot = useMemo(
        () => (
            <WebSidebarBottomSlot
                connectorOpen={connectorOpen}
                engineConnected={engineConnected}
                engineMode={engineMode}
                enginePanelActive={enginePanelActive}
                onToggleConnector={toggleConnector}
                settingsActive={settingsActive}
            />
        ),
        [
            connectorOpen,
            engineConnected,
            engineMode,
            enginePanelActive,
            settingsActive,
            toggleConnector,
        ],
    );

    // Register all commands
    useEffect(() => {
        const cmds = getCommonCommands({ t });

        for (const cmd of cmds) {
            // Skip palette.open — AppShell registers it with the real setPaletteOpen handler
            if (cmd.id === "palette.open") continue;
            registerCommand(cmd);
            if (cmd.shortcut) bindShortcut(cmd.shortcut, cmd.id);
        }
    }, [t]);

    useKeyboardShortcuts();

    return (
        <ExtensionHostBootstrap>
            <AppShell
                sidebarActivityItems={sidebarActivityItems}
                toolbarSlot={toolbarSlot}
                sidebarBottomSlot={sidebarBottomSlot}
                EngineConnectorPanel={connectorOpen ? EngineConnectorPanel : undefined}
                onCloseEngineConnectorPanel={closeConnector}
            />
        </ExtensionHostBootstrap>
    );
}
